import { useContext } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiContext } from '@shared/contexts';
import { TCreateDetectionReturn, TPollDetectionReturn } from '../types/shared.types';
import { EDetectionPollingStatus } from '../types/shared.types';
import { FAILED_DETECTION_STATUS } from '../constants';
import { IInlineBlockDetectionRun } from '../types/inlineBlocks.types';
import { IReferenceDetectionRun } from '../types/references.types';

type TMessages = {
  startedSuccess: string;
  startError: string;
  lockedWarning: string;

  submitSuccess: string;
  submitError: string;

  cancelSuccess: string;
  cancelError: string;

  loadErrorTitle: string;
  loadErrorToast: string;
};

type TConfig = {
  /** Namespace for cache keys, e.g. 'references' | 'inline-blocks' */
  ns: string;
  /** Base REST path, e.g. '/detections/references' */
  basePath: string;
  /** User-facing messages */
  messages: TMessages;
  /** Polling interval in ms (default 2000) */
  pollingMs?: number;
};

/** Uniform query key */
const runKey = (ns: string, runId?: string) => [`run-${ns}-${runId ?? 'new'}`];

export const makeDetectionApiHooks = <
  TCreateDto,
  IDetectionRun extends IInlineBlockDetectionRun | IReferenceDetectionRun,
  TSubmitDto,
>(
  cfg: TConfig
) => {
  const POLLING_INTERVAL = cfg.pollingMs ?? 2000;

  /* -------------------- create -------------------- */
  const useCreateDetection = () => {
    const { apiClient, show, handleApiError } = useContext(ApiContext);

    const mutationFn = async (payload: TCreateDto): Promise<TCreateDetectionReturn> =>
      apiClient.post<TCreateDetectionReturn>(cfg.basePath, payload).then((res) => res.data);

    return useMutation({
      mutationFn,
      onSuccess: (data) => {
        if ('lockedUserIds' in data) {
          show('warning', cfg.messages.lockedWarning);
        } else {
          show('success', cfg.messages.startedSuccess);
        }
      },
      onError: handleApiError(cfg.messages.startError),
    });
  };

  /* -------------------- poll -------------------- */
  const usePollDetection = (runId: string | undefined) => {
    const { apiClient, retry, useHandleQueryError } = useContext(ApiContext);

    const fetchFn = async (): Promise<TPollDetectionReturn<IDetectionRun> | undefined> => {
      if (!runId) return;

      const { data } = await apiClient.get<TPollDetectionReturn<IDetectionRun>>(`${cfg.basePath}/${runId}`);

      if (data.status === EDetectionPollingStatus.READY && FAILED_DETECTION_STATUS.includes(data.run.status))
        console.error(`Detection failed: ${data.run.errorMessage}!`);

      return data;
    };

    const query = useQuery({
      queryKey: runKey(cfg.ns, runId),
      queryFn: fetchFn,
      enabled: Boolean(runId),
      retry,
      refetchInterval: ({ state }) =>
        state.data?.status === EDetectionPollingStatus.PENDING ? POLLING_INTERVAL : false,
      refetchOnWindowFocus: false,
    });

    useHandleQueryError(query.isError, query.error, cfg.messages.loadErrorTitle, cfg.messages.loadErrorToast);

    return query;
  };

  /* -------------------- submit -------------------- */
  const useSubmitDetection = (runId: string) => {
    const { apiClient, show, handleApiError } = useContext(ApiContext);

    const mutationFn = async (payload: TSubmitDto): Promise<void> =>
      apiClient.put(`${cfg.basePath}/${runId}/submit`, payload);

    return useMutation({
      mutationFn,
      onSuccess: () => show('success', cfg.messages.submitSuccess),
      onError: handleApiError(cfg.messages.submitError),
    });
  };

  /* -------------------- cancel -------------------- */
  const useCancelDetection = () => {
    const { apiClient, show, handleApiError } = useContext(ApiContext);

    const mutationFn = async ({ runId }: { runId: string }): Promise<void> =>
      apiClient.put(`${cfg.basePath}/${runId}/cancel`);

    return useMutation({
      mutationFn,
      onSuccess: () => show('success', cfg.messages.cancelSuccess),
      onError: handleApiError(cfg.messages.cancelError),
    });
  };

  return { useCreateDetection, usePollDetection, useSubmitDetection, useCancelDetection };
};
